import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import { PrimaryButton } from '../../shared/ui';
import { restaurantRejectOrder } from '../../shared/services/orderService';

// Reject a Pending order — voids the JAD authorization via restaurantRejectOrder.
// iOS uses a plain confirm alert; the web version adds an optional reason so
// the cancellation is recorded on the order doc.
export default function RejectOrderSheet({ order, open, onClose }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setReason(''); setError(''); setBusy(false); }
  }, [open, order?.id]);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await restaurantRejectOrder(order, reason.trim());
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to reject order. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={busy ? () => {} : onClose} title="Reject Order">
      <div className="px-4 pb-6 space-y-4">
        <p className="text-sm text-gray-500">
          Are you sure you want to reject order <span className="font-semibold">#{String(order?.id || '').slice(0, 8)}</span>?
          The customer's payment will not be captured. This action cannot be undone.
        </p>
        <div>
          <label className="text-xs font-semibold text-gray-500">Reason (optional)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Restaurant is too busy"
            className="mt-1 w-full rounded-xl bg-gray-100 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-400/40 resize-none"
          />
        </div>
        {error && <p className="text-xs font-medium text-red-500">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-3 rounded-2xl bg-gray-100 text-gray-700 font-bold text-sm active:bg-gray-200 disabled:opacity-50"
          >
            Keep Order
          </button>
          <PrimaryButton color="bg-red-500" onClick={submit} disabled={busy} className="flex-1">
            {busy ? <Loader2 size={16} className="animate-spin inline" /> : 'Reject Order'}
          </PrimaryButton>
        </div>
      </div>
    </Sheet>
  );
}
